import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Shield, Clock,
  CheckCircle, Armchair, Headphones, Snowflake, Search, CreditCard, ChevronRight
} from 'lucide-react';
import { COMPANY_CONFIG } from '../config/config';

const Dashboard = () => {
  const coverImages = ['/images/A2.jpg', '/images/A3.jpg', '/images/A1.jpg'];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState(1);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % coverImages.length);
    }, 4000);

    const preloadLaterSlides = window.setTimeout(() => {
      setLoadedSlides(coverImages.length);
    }, 800);

    return () => {
      clearInterval(intervalId);
      clearTimeout(preloadLaterSlides);
    };
  }, [coverImages.length]);

  const highlights = [
    { title: 'Safe Travel', description: 'Your safety is our priority', icon: Shield },
    { title: 'Premium Comfort', description: 'Spacious seats', icon: Armchair },
    { title: 'On-Time Service', description: 'Trusted schedules', icon: Clock },
    { title: '24/7 Support', description: "We're always here to help", icon: Headphones }
  ];

  const popularRoutes = [
    { title: 'Colombo → Ampara', via: 'Via Kandy / Mahiyanganaya / Maha Oya / Bibile' },
    { title: 'Katunayake → Badulla', via: 'Via Kandy' },
    { title: 'Colombo → Badulla', via: 'Via Kandy' },
    { title: 'Colombo → Meegahakiula', via: 'Via Kandy / Mahiyanganaya' },
    { title: 'Colombo → Mahiyanganaya', via: 'Via Kandy' }
  ];

  const bookingSteps = [
    { title: 'Choose Route', description: 'Select your departure and destination', icon: Search },
    { title: 'Select Seat', description: 'Choose your preferred seat on Tripimate', icon: Armchair },
    { title: 'Pay Securely', description: 'Complete your payment online', icon: CreditCard },
    { title: 'Receive Confirmation', description: 'Get your e-ticket instantly', icon: CheckCircle }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-50">
      {/* Hero Carousel Section */}
      <section className="relative h-[260px] sm:h-[340px] lg:h-[540px] overflow-hidden bg-[#00336a]">
        {coverImages.map((image, index) => (
          <div
            key={image}
            className={`absolute inset-0 transition-opacity duration-700 ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: index < loadedSlides || index === currentSlide ? `url(${image})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
            aria-label={`Nuwantha cover slide ${index + 1}`}
          />
        ))}

        <div className="absolute bottom-4 left-0 right-0 z-10 hidden sm:flex justify-center gap-2">
          {coverImages.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              onClick={() => setCurrentSlide(index)}
              className={`h-2.5 w-2.5 rounded-full border border-white/50 transition-all ${
                currentSlide === index ? 'bg-white' : 'bg-white/40'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Popular Routes */}
      <section className="relative z-20 mt-2 sm:mt-3 pb-8 sm:pb-12 lg:pb-16 bg-gradient-to-b from-transparent via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <div>
          <div className="bg-white rounded-2xl shadow-lg border border-blue-100 px-3 sm:px-6 py-5 sm:py-6 mb-8 sm:mb-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-0">
              {highlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className={`flex flex-col items-center text-center px-2 sm:px-4 ${
                      index < highlights.length - 1 ? 'lg:border-r lg:border-gray-200' : ''
                    }`}
                  >
                    <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#6864ec] mb-2" strokeWidth={1.75} />
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base">{item.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm mt-0.5">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center mb-6 sm:mb-8">
            <p className="text-[#6864ec] font-semibold tracking-[0.2em] text-xs sm:text-sm mb-2">OUR ROUTES</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-2">
              Popular Routes
            </h2>
            <p className="text-sm sm:text-base text-gray-500">
              Select your route and book your seat online via Tripimate
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {popularRoutes.map((route) => (
              <div
                key={route.title}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow px-3 sm:px-5 py-3 sm:py-4 flex flex-row items-center gap-2.5 sm:gap-4"
              >
                <div className="flex items-start gap-2 sm:gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#eeedfc] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 sm:w-9 sm:h-9 text-[#6864ec]" strokeWidth={2.75} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-lg font-extrabold text-gray-900 leading-tight">{route.title}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm mt-0.5 leading-snug">{route.via}</p>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 bg-[#eeedfc] text-[#6864ec] text-[10px] sm:text-xs font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full">
                        <Snowflake className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        AC
                      </span>
                      <span className="inline-flex items-center gap-1 bg-[#eeedfc] text-[#6864ec] text-[10px] sm:text-xs font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full">
                        <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        Daily Service
                      </span>
                    </div>
                  </div>
                </div>
                <a
                  href="https://www.tripimate.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-0.5 sm:gap-1 bg-[#6864ec] hover:bg-[#5551d4] text-white font-bold text-[11px] sm:text-sm px-2.5 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-sm transition-colors shrink-0 whitespace-nowrap"
                >
                  Book Now
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </a>
              </div>
            ))}
          </div>
          </div>

          <div className="mt-8 sm:mt-10 bg-[#eeedfc] rounded-3xl px-2 sm:px-8 py-6 sm:py-10">
            <div className="text-center mb-5 sm:mb-8 px-1">
              <p className="text-[#6864ec] font-semibold tracking-[0.2em] text-xs sm:text-sm mb-2">HOW TO BOOK</p>
              <h2 className="text-lg sm:text-2xl lg:text-3xl font-extrabold text-gray-900">
                Book Your Journey in 4 Simple Steps
              </h2>
            </div>
            <div className="grid grid-cols-4 gap-1 sm:gap-3 lg:gap-2">
              {bookingSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="relative flex flex-col items-center text-center min-w-0">
                    {index < bookingSteps.length - 1 && (
                      <ChevronRight className="hidden lg:block absolute -right-1 top-7 w-5 h-5 text-[#6864ec]/40" />
                    )}
                    <div className="relative mb-2 sm:mb-3">
                      <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-white shadow-sm flex items-center justify-center">
                        <Icon className="w-4 h-4 sm:w-7 sm:h-7 text-[#6864ec]" />
                      </div>
                      <span className="absolute -top-1 -left-1 w-4 h-4 sm:w-6 sm:h-6 rounded-full bg-[#6864ec] text-white text-[9px] sm:text-xs font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-[10px] sm:text-base leading-tight">{step.title}</h3>
                    <p className="text-gray-500 text-[9px] sm:text-sm mt-1 leading-snug">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section with Booking Button */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 text-center border-2 border-blue-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 mb-3 sm:mb-4">
              Ready to Book Your Journey?
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-5 sm:mb-6 lg:mb-8 max-w-2xl mx-auto">
              Book your seat online now and enjoy a comfortable journey across Sri Lanka
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5 sm:mb-6 lg:mb-8">
              <a
                href="https://www.tripimate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="relative bg-[#6864ec] text-white px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-bold hover:bg-[#5551d4] transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 inline-flex items-center justify-center space-x-1.5 sm:space-x-2 border-2 border-[#6864ec] hover:border-[#5551d4] overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                <span className="relative z-10">Book Online Now</span>
              </a>
              <Link
                to="/contact"
                className="border-2 border-[#6864ec] text-[#6864ec] px-5 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-bold hover:bg-[#6864ec] hover:text-white transition-all duration-200 inline-flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-6 text-gray-600">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Phone className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-[#6864ec]" />
                <span className="font-medium text-xs sm:text-sm lg:text-base">{COMPANY_CONFIG.phone}</span>
              </div>
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Mail className="w-4 h-4 sm:w-4.5 sm:h-4.5 lg:w-5 lg:h-5 text-[#6864ec]" />
                <span className="font-medium text-xs sm:text-sm lg:text-base">{COMPANY_CONFIG.email}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
